import { AddonIds } from 'src/app/services/addon-pricing/addon-pricing.service';

/** Keys GrowthBook may use for per-add-on removal body copy (read order = priority). */
const ADDON_REMOVAL_TEXT_FIELD_KEYS = [
  'removalText',
  'removal_text',
  'removalCopy',
  'removal_copy',
  'RemovalText',
  'RemovalCopy',
] as const;

/**
 * Reads per-add-on removal copy from GrowthBook `addons-list` JSON.
 * Tries common key spellings (camelCase, snake_case, PascalCase); trims; non-strings → undefined.
 */
export function normalizeAddonsListRemovalText(addon: Record<string, unknown>): string | undefined {
  for (const key of ADDON_REMOVAL_TEXT_FIELD_KEYS) {
    const raw = addon[key];
    if (typeof raw !== 'string') {
      continue;
    }
    const t = raw.trim();
    if (t.length > 0) {
      return t;
    }
  }
  return undefined;
}

/** Shallow clone without raw GB removal keys so only normalized `removalText` remains on `AddonItem`. */
export function stripAddonRemovalRawFields<T extends Record<string, unknown>>(addon: T): T {
  const next = { ...addon };
  const deletable = next as Record<string, unknown>;
  for (const key of ADDON_REMOVAL_TEXT_FIELD_KEYS) {
    delete deletable[key];
  }
  return next;
}

// Addon-related type definitions
export type AddonItem = {
  imgSrc: string;
  imgSrcSummary?: string;
  imgAlt: string;
  imgWidth: number;
  title: string;
  price: string;
  subtitle: string;
  description: string;
  linkname: string;
  /** Per-add-on copy from GrowthBook `addons-list` (see `normalizeAddonsListRemovalText`). */
  removalText?: string;
  selfServeAddon?: boolean;
  addonIds?: AddonIds;
  purchased?: boolean;
  activationIn?: string;
  // Add dynamic pricing fields
  unitPrice?: number;
  billingPeriod?: 'yearly' | 'semi_yearly' | 'quarterly' | 'monthly';
  calculatedPrice?: string;
  calculatedSubtitle?: string;
  // Add new property to track if addon is being removed
  isBeingRemoved?: boolean;
};

export type AddonsResponse = {
  flowId: string;
  addons: AddonItem[];
};
